import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Carrito Keranimal.
 *
 * - Añadir al carrito desde las fichas propias (formularios /cart/add que no son del tema) abre
 *   el cajón al momento, sin recargar la página.
 * - Los botones «Añadir» de «Completa tu pedido» ([data-cpc-add]).
 * - <cpc-eta>: fecha estimada de entrega en días laborables, en el idioma de la tienda.
 * - <cpc-sticky-cta>: barra fija de pago en /cart (móvil) cuando el botón principal no se ve.
 *
 * Todo se comunica con el tema por el evento estándar `shopify:cart:lines-update`, igual que
 * product-form.js: así el cajón se abre solo y las secciones del carrito se repintan.
 */

const routes = () => (window.Theme && window.Theme.routes) || {};
// el tema ya da la ruta de añadir con «.js» (snippets/scripts.liquid); la de cambiar, sin él
const cartAddUrl = () => routes().cart_add_url || '/cart/add.js';
const cartChangeUrl = () => `${routes().cart_change_url || '/cart/change'}.js`;
const cartUrl = () => routes().cart_url || '/cart';

/** @returns {string[]} */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter((id) => Boolean(id));
}

async function fetchCart() {
  const response = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`cart ${response.status}`);
  return response.json();
}

/**
 * Añade líneas al carrito como lo hace el tema.
 * @param {Element} source - elemento desde el que sale el evento (burbujea hasta document)
 * @param {Array<{id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number}>} items
 */
export async function addLines(source, items) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: cartSectionIds().join(',') }),
    });
    const data = await response.json();

    if (data.status) {
      source.dispatchEvent(
        new CartErrorEvent({
          error: data.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: data.description, errors: data.errors },
        })
      );
      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'cp-cart' },
      });
      throw new Error(data.description || data.message || 'Add to cart failed');
    }

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        sections: data.sections,
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        source: 'cp-cart',
        didError: false,
      },
    });
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * Cambia la cantidad de una línea (0 = quitarla), como el carrito del tema: avisa con
 * shopify:cart:lines-update y entrega las secciones nuevas para repintar totales al momento.
 * @param {Element} source
 * @param {string} key - clave de la línea (item.key)
 * @param {number} quantity
 */
export async function changeLine(source, key, quantity) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: [{ id: key, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(cartChangeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity, sections: cartSectionIds().join(','), sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart change failed');
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: { items: data.items, sections: data.sections, source: 'cp-cart', didError: false },
    });
    return data;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/* ── Extras del carrito: protección de envío y aportación a Kare One ── */

// Casillas SIN MARCAR por defecto (cp-cart-extras.liquid). Marcar añade la línea y desmarcar la
// quita; el total, la barra de envío y el resto del carrito se repintan en el acto.
document.addEventListener('change', async (event) => {
  const box = event.target;
  if (!(box instanceof HTMLInputElement) || !box.matches('.cp-extras__check')) return;
  const wrap = box.closest('[data-cp-extras]');
  const wanted = box.checked;
  wrap?.classList.add('is-cargando');
  box.disabled = true;

  try {
    if (wanted) {
      const id = Number(box.dataset.variant);
      if (!id) throw new Error('sin variante');
      await addLines(box, [{ id, quantity: 1 }]);
    } else if (box.dataset.key) {
      await changeLine(box, box.dataset.key, 0);
    }
  } catch {
    box.checked = !wanted;
  } finally {
    box.disabled = false;
    wrap?.classList.remove('is-cargando');
  }
});

/* ── «Completa tu pedido» ─────────────────────────────────────── */

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('[data-cpc-add]');
  if (!(button instanceof HTMLButtonElement) || button.classList.contains('is-busy')) return;
  event.preventDefault();

  const card = button.closest('.cpc-up__card');
  const select = card?.querySelector('[data-cpc-variant]');
  const id = Number(select instanceof HTMLSelectElement ? select.value : button.dataset.cpcAdd);
  if (!id) return;

  button.classList.add('is-busy');
  button.setAttribute('aria-busy', 'true');
  try {
    await addLines(button, [{ id, quantity: 1 }]);
    button.classList.add('is-done');
  } catch {
    button.classList.remove('is-done');
  } finally {
    button.classList.remove('is-busy');
    button.removeAttribute('aria-busy');
  }
});

/* ── Fichas propias: añadir sin recargar y abrir el cajón ────── */

const ADD_ACTION = /\/cart\/add(\.js)?(\?|$)/;

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || event.defaultPrevented) return;
  if (!ADD_ACTION.test(form.getAttribute('action') || '')) return;
  // el formulario del propio tema ya hace todo esto
  if (form.closest('product-form-component')) return;
  // sin cajón (p. ej. en /cart) se mantiene el envío clásico
  if (!document.querySelector('cart-drawer-component')) return;

  const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
  let data;
  try {
    data = new FormData(form, submitter ?? undefined);
  } catch {
    data = new FormData(form);
  }
  const id = Number(data.get('id'));
  if (!id) return;
  event.preventDefault();

  /** @type {{id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number}} */
  const item = { id, quantity: Math.max(1, Number(data.get('quantity')) || 1) };
  /** @type {Record<string, string>} */
  const properties = {};
  for (const [key, value] of data) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
  }
  if (Object.keys(properties).length) item.properties = properties;
  const plan = data.get('selling_plan');
  if (plan) item.selling_plan = Number(plan);

  const button =
    submitter instanceof HTMLButtonElement ? submitter : form.querySelector('button[type="submit"], button:not([type])');
  if (button instanceof HTMLButtonElement) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('cpc-busy');
  }

  try {
    await addLines(form, [item]);
  } catch {
    // si Shopify rechaza la línea (sin stock, cantidad máxima…), el envío clásico muestra su mensaje
    HTMLFormElement.prototype.submit.call(form);
  } finally {
    if (button instanceof HTMLButtonElement) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.classList.remove('cpc-busy');
    }
  }
});

/* ── Fecha estimada de entrega ───────────────────────────────── */

/**
 * @param {Date} start
 * @param {number} days - días laborables (lunes a viernes)
 */
function addBusinessDays(start, days) {
  const date = new Date(start);
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const weekday = date.getDay();
    if (weekday !== 0 && weekday !== 6) added += 1;
  }
  while (date.getDay() === 0 || date.getDay() === 6) date.setDate(date.getDate() + 1);
  return date;
}

class CpcEta extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    this.#render();
    // el carrito se repinta con el texto del servidor: se vuelve a poner la fecha
    this.#observer = new MutationObserver(() => this.#render());
    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #render() {
    const target = this.querySelector('[data-cpc-eta]');
    const text = this.#text();
    if (!target || !text || target.textContent === text) return;
    target.textContent = text;
  }

  #text() {
    const min = Number(this.dataset.min ?? 1);
    const max = Math.max(min, Number(this.dataset.max ?? 2));
    const template = this.dataset.between || '{from} – {to}';
    try {
      const format = new Intl.DateTimeFormat(document.documentElement.lang || 'es', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'Europe/Madrid',
      });
      const label = (/** @type {Date} */ date) => format.format(date).replace(',', '');
      const now = new Date();
      return template.replace('{from}', label(addBusinessDays(now, min))).replace('{to}', label(addBusinessDays(now, max)));
    } catch {
      return '';
    }
  }
}

if (!customElements.get('cpc-eta')) customElements.define('cpc-eta', CpcEta);

/* ── Pie de pago fijo en el cajón ────────────────────────────── */

// El cajón decide si su pie va fijo comparando su alto con el del cajón, pero al pasar de vacío
// a lleno lo mide antes de que existan los totales y lo deja suelto (el botón de pagar acababa
// al final de la lista). Se vuelve a medir cada vez que cambia su contenido.
function fixDrawerSummary() {
  const dialog = document.querySelector('#cart-drawer dialog');
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
  const summary = dialog.querySelector('.cart-drawer__summary');
  if (!summary) return;
  const ratio = summary.getBoundingClientRect().height / (dialog.getBoundingClientRect().height || 1);
  dialog.setAttribute('cart-summary-sticky', ratio > 0.5 ? 'false' : 'true');
}

// Los botones − / + del carrito se activan o desactivan en el navegador (según mínimo y máximo,
// que incluye el stock real); al repintar el carrito vuelven como los manda el servidor, así que
// se recalculan tras cada cambio.
function refreshQuantityButtons() {
  for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
    const component = /** @type {any} */ (selector);
    if (typeof component.updateButtonStates === 'function') component.updateButtonStates();
  }
}

let cartFrame = 0;
const scheduleCartRefresh = () => {
  cancelAnimationFrame(cartFrame);
  cartFrame = requestAnimationFrame(() => {
    fixDrawerSummary();
    refreshQuantityButtons();
  });
};

const drawer = document.getElementById('cart-drawer');
if (drawer) {
  new MutationObserver(scheduleCartRefresh).observe(drawer, { childList: true, subtree: true });
  drawer.addEventListener('theme-drawer:open', scheduleCartRefresh);
}
const cartPage = document.querySelector('.cart-page')?.closest('cart-items-component');
if (cartPage) new MutationObserver(scheduleCartRefresh).observe(cartPage, { childList: true, subtree: true });

/* ── Barra fija de pago en /cart ─────────────────────────────── */

class CpcStickyCta extends HTMLElement {
  #frame = 0;
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const schedule = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#update());
    };
    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    // tras repintar el carrito, el servidor la devuelve oculta: se recalcula
    document.addEventListener(
      'shopify:cart:lines-update',
      (event) => {
        const promise = /** @type {any} */ (event).promise;
        if (promise && typeof promise.then === 'function') promise.then(schedule, schedule);
      },
      { signal }
    );
    schedule();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
    cancelAnimationFrame(this.#frame);
  }

  #update() {
    const main = document.querySelector('.cart-page #checkout');
    if (!main) return;
    const rect = main.getBoundingClientRect();
    const visible = rect.top < window.innerHeight && rect.bottom > 0;
    this.toggleAttribute('data-hidden', visible);
    this.toggleAttribute('inert', visible);
  }
}

if (!customElements.get('cpc-sticky-cta')) customElements.define('cpc-sticky-cta', CpcStickyCta);
